import {
  GraphQLField,
  GraphQLOutputType,
  GraphQLNonNull,
} from 'graphql';
import { cloneDeep } from 'lodash';

import TypeInspector from '@/classes/graphql-introspection/TypeInspector';
import PimcoreEntityIntrospection from '../graphql-introspection/PimcoreEntityIntrospection';
import { SchemaConstructor, TypeExtractionRules } from './types';
import GraphQLFieldsExtractor from '../graphql-introspection/GraphQLFieldsExtractor';

export default class EntitySchemaConstructor implements SchemaConstructor {
  constructor(
    private pimcoreEntityIntrospection: PimcoreEntityIntrospection,
    private graphQLFieldsExtractor: GraphQLFieldsExtractor,
    public extendFields = {
      getApplicationHRListing: {
        fields: '$sortBy: [String], $sortOrder: [String], $ids: String',
        values: 'sortBy: $sortBy, sortOrder: $sortOrder, ids: $ids',
      },
    },
  ) { }

  get singleQueryName() {
    return `get${this.pimcoreEntityIntrospection.entity}`;
  }

  get listingQueryName() {
    return this.pimcoreEntityIntrospection.entityListingQueryName;
  }

  constructSingleSchema(): string {
    return `query GetEntity($id: Int!) {
      ${this.singleQueryName} (id: $id) {
        ${this.parseQueryFields()}
      }
    }`;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructListingSchema(rules: TypeExtractionRules = { maxDepth: 3, byObject: null }) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    // eslint-disable-next-line max-len
    const fields = this.extendFields[this.pimcoreEntityIntrospection.entityListingQueryName] ? `, ${this.extendFields[this.pimcoreEntityIntrospection.entityListingQueryName].fields}` : '';
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    // eslint-disable-next-line max-len
    const values = this.extendFields[this.pimcoreEntityIntrospection.entityListingQueryName] ? `, ${this.extendFields[this.pimcoreEntityIntrospection.entityListingQueryName].values}` : '';

    const query = `query GetEntityListing($first: Int, $after: Int, $filter: String${fields}) {
      ${this.pimcoreEntityIntrospection.entityListingQueryName} (first: $first, after: $after, filter: $filter${values}) {
          items: edges {
            node {
              ${this.parseQueryFields(rules)}
            }
          }
          total: totalCount
        }
      }`;
    return query;
  }

  private parseQueryFields(rules?: TypeExtractionRules) {
    const fields = this.extractType(this.pimcoreEntityIntrospection.queryFields, { rules, depth: 0, handledObjectTypes: new Map() });
    return fields.join(' ');
  }

  // eslint-disable-next-line max-len
  private extractType(fields: GraphQLField<any, any, any>[] = [], options: { rules?: TypeExtractionRules; depth: number; handledObjectTypes: Map<string, number>; currentByObject?: any }): any {
    const byObject = options?.currentByObject || options?.rules?.byObject;
    let byObjectKeys: Array<string> | null = null;
    if (byObject) {
      options.currentByObject = byObject;
      byObjectKeys = Object.keys(options?.currentByObject);
    }
    options.depth = (options.depth !== null || options.depth !== undefined) ? options.depth + 1 : 0;
    if (!byObject && options?.rules?.maxDepth && options.depth > options.rules.maxDepth) {
      return [];
    }
    return fields.map((field) => {
      if (byObjectKeys !== null) {
        if (byObjectKeys.length === 0 || !byObjectKeys.includes(field.name)) {
          return '';
        }
      }
      const handledObjectTypes = new Map(options.handledObjectTypes);
      let inspectedType: GraphQLOutputType = field.type;

      if (TypeInspector.isListType(inspectedType)) {
        inspectedType = inspectedType.ofType;
      }

      if (TypeInspector.isUnionType(inspectedType)) {
        inspectedType = inspectedType.getTypes()[0];
        const counter = handledObjectTypes.get(inspectedType.name) || 0;
        handledObjectTypes.set(inspectedType.name, counter + 1);
      }

      if (TypeInspector.isObjectType(inspectedType)) {
        const fieldFields = this.graphQLFieldsExtractor.extractFrom(inspectedType);
        const counter = handledObjectTypes.get(inspectedType.name) || 0;
        if (counter > 1) {
          return `${field.name} {
            ... on ${inspectedType.name} {
              id
            }
          }`;
        }

        // temporarely removing fields with required arguments
        if (this.isArgsRequired(field)) {
          return '';
        }

        const extractTypeArray = this.extractType(fieldFields, cloneDeep({
          rules: {
            maxDepth: options?.rules?.maxDepth || 0,
            byObject: options?.rules?.byObject,
          },
          depth: options.depth,
          handledObjectTypes,
          currentByObject: byObject ? options?.currentByObject[field.name] : null,
        }));

        if (!extractTypeArray || !extractTypeArray.length) {
          return '';
        }

        /* eslint-disable */
        return `${field.name} {
          ... on ${inspectedType.name} {
            ${extractTypeArray.join(' ')}
          }
        }`;
        /* eslint-enable */
      }

      return `${field.name}\n`;
    });
  }

  private isArgsRequired(field: GraphQLField<any, any, any>): boolean {
    if (!field?.args?.length) {
      return false;
    }
    for (let i = 0; i < field.args.length; i += 1) {
      const arg = field.args[i];
      if (arg.type instanceof GraphQLNonNull) {
        return true;
      }
    }
    return false;
  }
}
